// When the app asks about the offline catalogue. The download is an opt-in (design doc decision 1:
// 20 to 40 MB is acceptable, asked once, and deliberately not tied to Wi-Fi: the person decides),
// so a device that said "not now" is never asked again; About always has the button. A device
// that has the catalogue is told once per new version, and never downloads a full one unasked. A
// small update (manager.ts) is the exception: it is applied in the background, nothing is asked.
import { useSyncExternalStore } from "react";
import { isDruifEnabled, isEdgeEnabled } from "../backends";
import { checkCatalogue, downloadCatalogue, getCatalogueState, useCatalogueState } from "./manager";
import { slowConnection, updateBlocker } from "./network";

const PREFS_KEY = "proef.catalogue.prompt";

export type CataloguePromptKind = "first" | "update";

interface Prefs {
  /** The first-run question was shown and answered either way. */
  firstAsked: boolean;
  /** The newest version the person put off. */
  updateDeclined: string | undefined;
  /** The catalogue was fetched unasked once on this device; it is never again, only on demand. */
  autoTried: boolean;
}

function readPrefs(): Prefs {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(PREFS_KEY) ?? "null");
    if (typeof raw === "object" && raw !== null) {
      const first: unknown = "firstAsked" in raw ? raw.firstAsked : undefined;
      const declined: unknown = "updateDeclined" in raw ? raw.updateDeclined : undefined;
      const auto: unknown = "autoTried" in raw ? raw.autoTried : undefined;
      return {
        firstAsked: first === true,
        updateDeclined: typeof declined === "string" ? declined : undefined,
        autoTried: auto === true,
      };
    }
  } catch {
    // Blocked storage or a damaged value: ask again, which is harmless.
  }
  return { firstAsked: false, updateDeclined: undefined, autoTried: false };
}

let prefs: Prefs =
  typeof window === "undefined"
    ? { firstAsked: false, updateDeclined: undefined, autoTried: false }
    : readPrefs();
const listeners = new Set<() => void>();

function setPrefs(next: Prefs): void {
  prefs = next;
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  } catch {
    // Not persisting means the question comes back next session.
  }
  listeners.forEach((listener) => {
    listener();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The first-run answer is final whichever way it went: a failed download must not nag either. */
export function answerFirstPrompt(): void {
  setPrefs({ ...prefs, firstAsked: true });
}

/** Puts off `version`; the next one asks again. */
export function declineUpdate(version: string): void {
  setPrefs({ ...prefs, updateDeclined: version });
}

/** Test seam: re-read the answers from storage, as a fresh page load would. */
export function reloadPromptPrefs(): void {
  prefs =
    typeof window === "undefined"
      ? { firstAsked: false, updateDeclined: undefined, autoTried: false }
      : readPrefs();
  listeners.forEach((listener) => {
    listener();
  });
}

/** Which question to show right now, if any. */
export function useCataloguePrompt(): CataloguePromptKind | undefined {
  const state = useCatalogueState();
  const current = useSyncExternalStore(
    subscribe,
    () => prefs,
    () => prefs,
  );
  // A download started from the strip keeps it on screen, as its progress readout.
  if (state.task?.kind === "downloading") return state.installed === undefined ? "first" : "update";
  // A failure is explained in About; popping a strip over it as well would only nag.
  if (state.offer === undefined || state.error !== undefined) return undefined;
  if (state.installed === undefined) return current.firstAsked ? undefined : "first";
  return current.updateDeclined === state.offer.version ? undefined : "update";
}

/**
 * With no druif to ask the catalogue is the backend, so a first launch fetches it unprompted, once,
 * unless the connection is metered, slow or asked to save data: then the first search or entity page
 * fetches it (lib/api.ts), with progress on screen. Resolves to whether a download was started.
 */
function autoInstall(): boolean {
  if (prefs.autoTried || getCatalogueState().installed !== undefined) return false;
  if (!isEdgeEnabled() || isDruifEnabled()) return false;
  if (updateBlocker() !== undefined || slowConnection()) return false;
  setPrefs({ ...prefs, autoTried: true });
  void downloadCatalogue();
  return true;
}

function checkInBackground(): void {
  if (!isEdgeEnabled()) return;
  if (getCatalogueState().installed === undefined && prefs.firstAsked) return;
  void checkCatalogue({ quiet: true });
}

let watching = false;

/**
 * Called once after the first paint: learns what the edge offers, when something could come of it
 * (a catalogue to update, or the first-run question still unasked), and keeps asking while the app
 * stays open: whenever it comes back to the foreground or the device comes back online. The check
 * itself is skipped if the edge is switched off or was asked in the last twelve hours (manager.ts),
 * and an unchanged manifest costs one conditional request that the edge answers with an empty 304.
 */
export function startCatalogue(): void {
  if (!autoInstall()) checkInBackground();
  if (watching || typeof window === "undefined") return;
  watching = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkInBackground();
  });
  window.addEventListener("online", checkInBackground);
}

/** Test seam: forget that the listeners were installed. */
export function resetCatalogueWatchForTests(): void {
  watching = false;
}
